import type {
  PublicCompany,
  PublicEstimatePayload,
  PublicInvoicePayload,
} from '@/components/documents/adapters';
import {
  parseLineItems,
  type PortalCompanyRow,
  type PortalEstimateRow,
  type PortalInvoiceRow,
  type PortalJobRow,
} from './hooks/portalClient';

export function toPublicCompany(company: PortalCompanyRow): PublicCompany {
  return {
    name: company.name,
    legal_name: company.legal_name,
    trading_name: company.trading_name,
    logo_url: company.logo_url,
    phone: company.phone,
    email: company.email,
    website: company.website,
    primary_color: company.primary_color,
    abn: company.abn,
    acn: company.acn,
    country: company.country,
    street_address: company.street_address,
    suburb: company.suburb,
    state: company.state,
    postcode: company.postcode,
    payment_instructions: company.payment_instructions,
    invoice_terms: company.invoice_terms,
    warranty_text: company.warranty_text,
  };
}

// Snapshot first (historical rate), live company config as the fallback.
function snapshotTax(
  row: { tax_rate_snapshot: number | null; tax_label_snapshot: string | null; tax_inclusive_snapshot: boolean | null },
  company: PortalCompanyRow,
) {
  if (row.tax_label_snapshot != null || row.tax_rate_snapshot != null) {
    return { rate: row.tax_rate_snapshot, label: row.tax_label_snapshot, inclusive: row.tax_inclusive_snapshot };
  }
  return company.tax_enabled
    ? { rate: company.tax_rate, label: company.tax_label, inclusive: company.tax_inclusive }
    : { rate: 0, label: null, inclusive: null };
}

export function toPublicEstimatePayload(row: PortalEstimateRow, company: PortalCompanyRow): PublicEstimatePayload {
  return {
    estimateNumber: row.estimate_number,
    customerName: row.customer_name ?? '',
    customerAddress: row.customer_address,
    serviceDescription: row.service_description,
    lineItems: parseLineItems(row.line_items),
    subtotal: Number(row.subtotal) || 0,
    gstAmount: Number(row.gst_amount) || 0,
    estimateAmount: Number(row.estimate_amount) || 0,
    validUntil: row.valid_until,
    terms: row.terms,
    createdAt: row.created_at,
    tax: snapshotTax(row, company),
    company: toPublicCompany(company),
  };
}

export function toPublicInvoicePayload(
  row: PortalInvoiceRow,
  company: PortalCompanyRow,
  job?: PortalJobRow | null,
): PublicInvoicePayload {
  return {
    invoiceNumber: row.invoice_number,
    status: row.status,
    issueDate: row.issue_date,
    dueDate: row.due_date,
    paidDate: row.paid_date,
    customerName: row.customer_name ?? '',
    customerAddress: row.customer_address,
    serviceAddress: row.service_address,
    lineItems: parseLineItems(row.line_items),
    subtotal: Number(row.subtotal) || 0,
    gstAmount: Number(row.gst_amount) || 0,
    totalAmount: Number(row.total_amount) || 0,
    amountPaid: Number(row.amount_paid) || 0,
    amountOutstanding: row.amount_outstanding,
    terms: row.terms,
    paymentUrl: row.payment_url,
    jobNumber: job?.job_number ?? null,
    serviceDate: job?.job_date ?? job?.scheduled_date ?? null,
    workSummary: job?.work_summary ?? null,
    tax: snapshotTax(row, company),
    company: toPublicCompany(company),
  };
}
